function formatDocumentHashValue(cert) {
  const h = cert?.documentHash
  if (h == null || h === '' || h === 'Not available') return '—'
  return String(h)
}

/**
 * Adds a minimal electronic signature certificate page to a jsPDF instance (pt, A4).
 * @param {import('jspdf').jsPDF} pdf
 * @param {{
 *   documentName: string,
 *   signerName: string,
 *   signerEmail: string,
 *   ipAddress: string,
 *   timestampIso: string,
 *   documentId: string,
 *   documentHash?: string,
 *   consentConfirmed: boolean,
 * }} cert
 */
export function appendCompletionCertificatePage(pdf, cert) {
  pdf.addPage()
  drawCompletionCertificateOnCurrentPage(pdf, cert)
}

/**
 * @param {import('jspdf').jsPDF} pdf
 * @param {Parameters<appendCompletionCertificatePage>[1]} cert
 */
export function drawCompletionCertificateOnCurrentPage(pdf, cert) {
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  const margin = 48
  const maxW = pageW - margin * 2
  const lineH = 13
  let y = margin + 6

  const safe = (v) => (v == null || v === '' ? '—' : String(v))

  const flushParagraph = (text, fontSize, fontStyle) => {
    pdf.setFontSize(fontSize)
    pdf.setFont('helvetica', fontStyle)
    const lines = pdf.splitTextToSize(text, maxW)
    for (let i = 0; i < lines.length; i++) {
      if (y > pageH - margin - lineH) {
        pdf.addPage()
        y = margin + 6
      }
      pdf.text(lines[i], margin, y)
      y += lineH
    }
    y += 4
  }

  pdf.setTextColor(15, 23, 42)
  flushParagraph('Electronic Signature Certificate of Completion', 18, 'bold')
  pdf.setTextColor(51, 65, 85)
  flushParagraph(
    'This certificate summarizes the electronic signing event for the document below. It is provided for your records.',
    10,
    'normal',
  )

  pdf.setTextColor(15, 23, 42)
  const rows = [
    ['Document name', safe(cert.documentName)],
    ['Signer name', safe(cert.signerName)],
    ['Signer email', safe(cert.signerEmail)],
    ['IP address', safe(cert.ipAddress)],
    ['Timestamp (UTC)', safe(cert.timestampIso)],
    ['Document ID', safe(cert.documentId)],
    ['Document Hash (SHA-256)', formatDocumentHashValue(cert)],
    [
      'Electronic signature consent',
      cert.consentConfirmed
        ? 'Signer agreed to use electronic signatures and accepted that the signature is legally binding under applicable laws.'
        : '—',
    ],
  ]

  for (const [label, value] of rows) {
    pdf.setFontSize(9)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(100, 116, 139)
    const labelLines = pdf.splitTextToSize(label, maxW)
    for (const ln of labelLines) {
      if (y > pageH - margin - lineH) {
        pdf.addPage()
        y = margin + 6
      }
      pdf.text(ln, margin, y)
      y += lineH - 1
    }
    pdf.setFontSize(11)
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(15, 23, 42)
    const valueLines = pdf.splitTextToSize(value, maxW)
    for (const ln of valueLines) {
      if (y > pageH - margin - lineH) {
        pdf.addPage()
        y = margin + 6
      }
      pdf.text(ln, margin, y)
      y += lineH
    }
    y += 6
  }

  pdf.setFontSize(9)
  pdf.setFont('helvetica', 'italic')
  pdf.setTextColor(148, 163, 184)
  flushParagraph('Generated by Prime Document Sign.', 9, 'italic')
}
